import { Button } from '@/components/ui/button';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog';

/**
 * Generic confirm/dismiss modal — two CTAs (primary + secondary), no
 * close (X) button. Caller-supplied copy + handlers; the pattern is
 * intentionally string-prop-driven so route-level STRINGS lookups stay
 * at the route, not in the pattern.
 *
 * Built on shadcn `<Dialog>` (radix-based). Promoted from Batch 9A's
 * `account/CancelConfirmModal.tsx` per the FB-5 ≥2-consumer rule —
 * Batch 9B Delete-Child-Confirm becomes the 2nd consumer alongside
 * the refactored CancelConfirm.
 *
 * `variant='destructive'` swaps the primary CTA to the destructive
 * Button variant for delete-style flows. `variant='neutral'` (default)
 * preserves Batch 9A CancelConfirm visual (gold primary CTA).
 *
 * `testId` is a **raw** prefix passed to the dialog content; callers
 * compose the full selector ("cancel-confirm-modal",
 * "delete-child-confirm-modal", …) so existing test selectors stay
 * stable across consumers.
 */
export interface ConfirmModalProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  headline: string;
  /** Body copy. Caller resolves any {placeholder} interpolations before passing. */
  body: string;
  primaryCta: string;
  secondaryCta: string;
  /** Fired AFTER the modal closes, when the user taps the primary CTA. */
  onConfirm: () => void;
  /**
   * Optional — fires AFTER the modal closes, when the user taps the
   * secondary CTA (button click only — Esc / backdrop / programmatic
   * close still close silently). When omitted, secondary just closes
   * (Batch 9A CancelConfirmModal preserved behavior).
   *
   * Use for two-action confirm modals (e.g. Batch 9B A-11 dirty-switch:
   * "Speichern und wechseln" / "Verwerfen und wechseln" — both fire
   * actions, neither is a pure cancel).
   */
  onSecondaryAction?: () => void;
  variant?: 'neutral' | 'destructive';
  /** Raw — caller composes the full selector. */
  testId?: string;
}

export function ConfirmModal({
  open,
  onOpenChange,
  headline,
  body,
  primaryCta,
  secondaryCta,
  onConfirm,
  onSecondaryAction,
  variant = 'neutral',
  testId,
}: ConfirmModalProps) {
  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent showCloseButton={false} data-testid={testId}>
        <DialogHeader>
          <DialogTitle>{headline}</DialogTitle>
          <DialogDescription>{body}</DialogDescription>
        </DialogHeader>
        <DialogFooter className="m-0 flex flex-col-reverse gap-2 border-0 bg-transparent p-0 sm:flex-row sm:justify-end">
          <Button
            type="button"
            variant="outline"
            onClick={() => {
              onOpenChange(false);
              onSecondaryAction?.();
            }}
          >
            {secondaryCta}
          </Button>
          <Button
            type="button"
            variant={variant === 'destructive' ? 'destructive' : 'default'}
            onClick={() => {
              onOpenChange(false);
              onConfirm();
            }}
          >
            {primaryCta}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
